import $ from 'jquery';
import Swal from 'sweetalert2';
import {
  removePageIndex,
  updatePageNumbers,
  deleteEditorPage,
  addPagesToSidebar,
  goToPage,
} from '~src/multipages';
import { onErrorShowMessage } from '~src/errors';
import { getState, getStatePure, modifyState } from '~src/state';

export const handleDeletePageBtn = (event) => {
  event.preventDefault();
  const currentPageIndex = getState('currentPageIndex');
  const pages = getStatePure('pages');

  const editorPageIndex = $(event.currentTarget).data('page-btn-index');
  const editorPageUrl = $(event.currentTarget).data('page-url');

  // Prevent deleting the last page
  if (pages.length === 1) {
    return Swal.fire({
      title: 'Attenzione!',
      text: "Non puoi eliminare l'ultima pagina.",
      icon: 'warning',
      confirmButtonColor: '#278e8e',
    });
  }

  return Swal.fire({
    title: 'Sei sicuro?',
    imageUrl: editorPageUrl,
    imageHeight: 150,
    imageAlt: 'Pagina da cancellare',
    text: 'Vuoi cancellare questa pagina?',
    type: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#278e8e',
    cancelButtonColor: '#d33',
    cancelButtonText: 'No',
    confirmButtonText: 'Si, elimina',
  }).then(async (result) => {
    if (result.value) {
      // Con un salvataggio in corso (anche l'autosave partito durante la
      // conferma) le pagine nuove vengono ancora create e collegate: toglierne
      // una adesso la lascerebbe collegata o, nei modelli, sposterebbe gli
      // objectId (assegnati per indice) sulle pagine sbagliate.
      if (getState('saving')) {
        return Swal.fire({
          title: 'Salvataggio in corso',
          text: 'Attendi qualche secondo e riprova a eliminare la pagina.',
          icon: 'warning',
          confirmButtonColor: '#278e8e',
        });
      }

      // Da qui alla fine nessun salvataggio (autosave, Ctrl+S) deve partire:
      // saveEditorState lo salta finché deletingPage è attivo.
      modifyState('deletingPage', true);

      Swal.fire({
        text: 'Sto eliminando la pagina...',
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        showConfirmButton: false,
        confirmButtonColor: '#278e8e',
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        let returnToIndex;

        // Determine which page to navigate to after deletion
        if (currentPageIndex === editorPageIndex && pages.length > 1) {
          // If deleting the current page, navigate away first
          const isLastPage = editorPageIndex === pages.length - 1;
          const tempNavigateIndex = isLastPage
            ? editorPageIndex - 1
            : editorPageIndex + 1;

          await goToPage(tempNavigateIndex);
        }

        // Calculate the final page index after deletion
        if (editorPageIndex < currentPageIndex) {
          // If deleting a page before current, current index will shift down
          returnToIndex = currentPageIndex - 1;
        } else if (editorPageIndex === currentPageIndex) {
          // If deleting current page
          const isLastPage = editorPageIndex === pages.length - 1;
          returnToIndex = isLastPage ? editorPageIndex - 1 : editorPageIndex;
        } else {
          // If deleting a page after current, current index stays the same
          returnToIndex = currentPageIndex;
        }

        // L'id va letto dallo stato e non dal bottone: la sidebar può essere
        // stata disegnata prima del salvataggio che ha assegnato l'objectId.
        const editorPageId = getStatePure('pages')[editorPageIndex]?.objectId;

        // First delete from backend
        await deleteEditorPage(editorPageId);

        // Then update local state
        await removePageIndex(editorPageIndex);
        await updatePageNumbers();

        // Show success message
        Swal.hideLoading();
        Swal.fire({
          title: 'Fatto!',
          html: 'La pagina &egrave; stata eliminata.',
          icon: 'success',
          confirmButtonColor: '#278e8e',
        });

        // Get the updated pages array after deletion
        const updatedPages = getStatePure('pages');

        // Update the sidebar with the new pages array
        await addPagesToSidebar();

        // Update current page index if needed
        if (editorPageIndex < currentPageIndex) {
          // If we deleted a page before the current page, adjust the index
          await modifyState('currentPageIndex', currentPageIndex - 1);
        } else if (
          editorPageIndex === currentPageIndex &&
          updatedPages.length > 0
        ) {
          // If we deleted the current page, set to a valid index
          const newIndex = Math.min(editorPageIndex, updatedPages.length - 1);
          await modifyState('currentPageIndex', newIndex);
        }

        // Navigate to the appropriate page
        const finalCurrentIndex = getState('currentPageIndex');
        if (updatedPages.length > 0 && returnToIndex !== finalCurrentIndex) {
          await goToPage(returnToIndex);
        }
      } catch (error) {
        console.error(error);
        onErrorShowMessage();
      } finally {
        modifyState('deletingPage', false);
      }
    }
  });
};
